import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Tag } from '../tags/types';
import type { AudioReferenceUsage } from '../../components/generation/state/types';

export type Direction = 'front' | 'left' | 'right' | 'back' | 'top' | 'bottom';

/**
 * 非ラスタ原本 (SVG 等) のラスタ版 (PNG) の状態 (docs/features/material-raster-rendition-plan.md §3.1)。
 * ラスタ原本 (PNG/JPEG) や動画・音声の素材には載らない (undefined)。
 *  - ready: ラスタ版あり (raster_url が使える)
 *  - failed: 描画に失敗 (自動再試行しない。`POST .../raster?force=1` で明示再試行)
 *  - missing: 未生成 (バックフィル前など。ラスタ版が要る操作の直前に lazy 生成する)
 */
export type MaterialRasterStatus = 'ready' | 'failed' | 'missing';

/** 素材一覧・単体取得の応答に載るラスタ版の情報 (非ラスタ原本のみ) */
export type MaterialRasterFields = {
  /** ラスタ版 (PNG) の URL。無ければ null */
  raster_url?: string | null;
  raster_status?: MaterialRasterStatus;
};

export type TemporaryMaterial = MaterialRasterFields & {
  id: string;
  asin?: string | string[];  // 配列対応
  directions: Partial<Record<Direction, string>>;
  thumbnailUrl?: string;
  thumbnail_url?: string;  // サムネイル画像URL
  previewUrl?: string;
  preview_url?: string;
  temp_path?: string;
  storage_path?: string;
  storage_bucket?: string;
  sourceUrl?: string;
  source_url?: string;
  original_url?: string;  // モーダル表示用の元ファイルURL
  public_url?: string;  // 公開URL
  width?: number;
  height?: number;
  createdAt?: string;
  created_at?: string;
  source_type?: string;
  content_type?: string;
  file_type?: string;  // ファイルタイプ（image, videoなど）
  duration_seconds?: number;
  file_size_bytes?: number;
  deleted_at?: string;
  notes?: string;
  name?: string | null;  // 表示名 (手動アップロードは元ファイル名、AI生成は null)
  tags?: Tag[];  // タグオブジェクトの配列に変更
  folder_type?: 'temporary' | 'cloud';  // フォルダタイプ（APIレスポンスから）
  protected?: boolean;  // 保護フラグ
  model_ids?: string[];  // AI俳優モデルID
  generation_metadata?: Record<string, unknown>;
  product_links?: { product_id: string }[];
  aspect_ratio?: string;
  workflow_stage_id?: string | null;  // 制作進捗 (organization_material_workflow_stages.id)
  /** 参照音声の既定用途 (voice/bgm/sound_effect/sound_logo)。音声以外は常に null
   *  (docs/features/reference-audio-roles-plan.md §2.5)。 */
  default_audio_role?: AudioReferenceUsage | null;
  /** ナレーション素材の生成元シナリオ (一覧 API のみ・source_type='narration' のときだけ載る。引けなければ null) */
  narration_scenario?: { id: string; title: string } | null;
};

export type CloudMaterial = MaterialRasterFields & {
  id: string;
  asin?: string | string[];  // 配列対応
  source_type?: string;
  sourceUrl?: string;
  source_url?: string;
  storage_bucket?: string;
  storage_path?: string;
  original_url?: string;  // モーダル表示用の元ファイルURL
  thumbnailUrl?: string;
  thumbnail_url?: string;  // サムネイル画像URL
  width?: number;
  height?: number;
  created_at?: string;
  updated_at?: string;
  tags?: Tag[];  // タグオブジェクトの配列に変更
  variants?: Array<{
    id: string;
    aspect_ratio?: string;
    storage_path?: string;
  }>;
  notes?: string;
  name?: string | null;  // 表示名 (手動アップロードは元ファイル名、AI生成は null)
  content_type?: string;
  preview_url?: string;
  previewUrl?: string;
  duration_seconds?: number;
  file_size_bytes?: number;
  deleted_at?: string;
  folder_type?: 'temporary' | 'cloud';  // フォルダタイプ（APIレスポンスから）
  protected?: boolean;  // 保護フラグ
  model_ids?: string[];  // AI俳優モデルID
  generation_metadata?: Record<string, unknown>;
  workflow_stage_id?: string | null;  // 制作進捗 (organization_material_workflow_stages.id)
  /** 参照音声の既定用途 (voice/bgm/sound_effect/sound_logo)。音声以外は常に null
   *  (docs/features/reference-audio-roles-plan.md §2.5)。 */
  default_audio_role?: AudioReferenceUsage | null;
  /** ナレーション素材の生成元シナリオ (一覧 API のみ・source_type='narration' のときだけ載る。引けなければ null) */
  narration_scenario?: { id: string; title: string } | null;
};

export type CreateCloudMaterialPayload = {
  asin?: string | string[];  // 配列対応
  source_type?: string;
  storage_bucket?: string;
  storage_path?: string;
  width?: number;
  height?: number;
  notes?: string;
  tags?: string[];  // タグIDの配列
};

export type UpdateCloudMaterialPayload = {
  asin?: string | string[];
  notes?: string;
  name?: string | null;  // 表示名 (空文字/null で解除)
  tags?: string[];  // タグIDの配列
  protected?: boolean;  // 保護フラグ
  workflow_stage_id?: string | null;  // 制作進捗 (null で解除)
};

export type UpdateTemporaryMaterialPayload = {
  asin?: string | string[];
  notes?: string;
  name?: string | null;  // 表示名 (空文字/null で解除)
  tags?: string[];  // タグIDの配列
  protected?: boolean;  // 保護フラグ
  workflow_stage_id?: string | null;  // 制作進捗 (null で解除)
  // 生成メタデータ。サーバー側でマージ保存される (BGM アップロード時の曲名など)
  generation_metadata?: Record<string, unknown>;
};

export type MaterialsState = {
  temporary: TemporaryMaterial[];
  cloud: CloudMaterial[];
  isLoading: boolean;
  error?: string;
};

const initialState: MaterialsState = {
  temporary: [],
  cloud: [],
  isLoading: false,
};

const materialsSlice = createSlice({
  name: 'materials',
  initialState,
  reducers: {
    setTemporaryMaterials(state, action: PayloadAction<TemporaryMaterial[]>) {
      state.temporary = action.payload;
    },
    setCloudMaterials(state, action: PayloadAction<CloudMaterial[]>) {
      state.cloud = action.payload;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setError(state, action: PayloadAction<string | undefined>) {
      state.error = action.payload;
    },
  },
});

export const { setTemporaryMaterials, setCloudMaterials, setLoading, setError } = materialsSlice.actions;
export default materialsSlice.reducer;
